import {
  Button,
  Distribute,
  FormField,
  IconButton,
  SvgClose,
  SvgPlusCircle,
  Text,
  Tooltip,
} from '@loomhq/lens';
import Mousetrap from 'mousetrap';
import React from 'react';
import { connect } from 'react-redux';

import { CUSTOM_VIEWERS_APPLIED } from 'constants/events';
import { ESCAPE, KEY_ESCAPE } from 'constants/keyCodes';
import { KEY_UP } from 'constants/keyEvents';
import { updateVideoSettings } from 'creators/shareVideo';
import { updateVideoPermissionList } from 'creators/video';
import * as analytics from 'utilities/analytics';

import { email as emailValidation } from '../../../../../shared/validate';

import SearchTypeahead from 'components/search-typeahead';

import './styles.less';

class CustomAccessSettings extends React.Component {
  static defaultProps = {
    onExitClick: () => {},
    onSaveClick: () => {},
  };

  state = {
    selections: [],
    removals: [],
  };

  render() {
    const { video, onExitClick, permissionList } = this.props;

    if (video == null) {
      return null;
    }

    const { selections, removals } = this.state;
    const listHasViewers = selections.length + permissionList.length > 0;
    const listHasChanged = !(selections.length === 0 && removals.length === 0);

    const currentPermissionList = permissionList.filter(
      sel => !removals.includes(sel)
    );

    const totalEmails = selections.length + currentPermissionList.length;

    return (
      <div className="custom-access-share-video-container">
        <div className="edit-custom-access-settings-section">
          <Text variant="title" className="mb:small">
            Share with specific people
          </Text>
          <Text color="bodyDimmed">
            Only allow people with specific emails to view this video. Login
            required.
          </Text>
        </div>
        <div className="edit-custom-access-settings">
          <FormField label="Add emails" className="mb:small">
            <SearchTypeahead
              onKeyUp={e => this.handleOnKeyUp(e)}
              newStyle={true}
              autoFocus={true}
              suggestions={[]}
              returnInputOnEmptySelection={true}
              queryValidation={this.emailValidation}
              onSelection={this.handleSelection}
              showCtaButton={false}
              showIconButton={true}
              iconButton={<SvgPlusCircle />}
            />
          </FormField>
          {listHasViewers && (
            <div
              className={`permitted-viewer-list ${
                // this is to ensure that the option background color stays as
                // white or gray as new options are added to the top of the list
                totalEmails % 2 == 0 ? 'odd' : 'even'
              }`}
            >
              {selections.map(sel => this.formatNewSelection(sel))}
              {currentPermissionList.map(sel => this.formatOldSelection(sel))}
            </div>
          )}
        </div>
        <div className="px:xlarge pt:medium pb:large">
          <Distribute isSpread>
            <Button variant="neutral" onClick={() => onExitClick()}>
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={e => this.handleSavePeopleClick(e)}
              isDisabled={!listHasChanged}
            >
              {selections.length > 0 ? 'Invite' : 'Save'}
            </Button>
          </Distribute>
        </div>
      </div>
    );
  }

  componentDidMount() {
    Mousetrap.bind(KEY_ESCAPE, this.props.onExitClick, KEY_UP);
  }

  componentWillUnmount() {
    Mousetrap.unbind(KEY_ESCAPE, KEY_UP);
  }

  getRemoveButton = onClick => (
    <div>
      <Tooltip
        content="Remove Email"
        placement="bottomCenter"
        triggerOffset={6}
      >
        <IconButton
          icon={<SvgClose />}
          altText="Remove email"
          onClick={onClick}
        />
      </Tooltip>
    </div>
  );

  handleOnKeyUp = e => {
    if (e.keyCode === ESCAPE) {
      this.props.onExitClick();
    }
  };

  formatOldSelection = selection => {
    const email = selection;
    const { removals } = this.state;

    const removeButton = this.getRemoveButton(() =>
      this.setState({ removals: [email, ...removals] })
    );

    return (
      <div className="viewer-list-entry" key={email}>
        {email}
        {removeButton}
      </div>
    );
  };

  formatNewSelection = selection => {
    const { selections } = this.state;

    const email = selection;

    const removeButton = this.getRemoveButton(() =>
      this.setState({
        selections: selections.filter(sel => sel !== email),
      })
    );

    return (
      <div className="viewer-list-entry" key={email}>
        {email}
        {removeButton}
      </div>
    );
  };

  handleSelection = ({ text }) => {
    const { selections } = this.state;

    this.setState({ selections: [text, ...selections] });
  };

  emailValidation = email => {
    const { permissionList, user } = this.props;
    const { selections } = this.state;

    if (user.email === email) {
      return "Oops! Can't add your own email";
    }

    if ([...permissionList, ...selections].includes(email)) {
      return 'Oops! That email is already in the list';
    }

    if (!emailValidation(email)) {
      return "Oops! That doesn't look like a valid email";
    }
  };

  handleSavePeopleClick = () => {
    const {
      video: { id: videoId },
    } = this.props;
    const { selections, removals } = this.state;

    const updates = selections
      .map(email => ({ email, status: true }))
      .concat(removals.map(email => ({ email, status: false })));

    if (updates.length > 0) {
      this.props.updateVideoPermissionList(updates, videoId);

      this.props.completeChecklistItem();

      analytics.track(CUSTOM_VIEWERS_APPLIED, {
        number_of_changes: updates.length,
      });
    }

    this.props.onExitClick();
  };
}

const mapStateToProps = state => {
  const {
    settings_modal_err: validationError,
    show_pw_settings_modal: show,
    video,
  } = state.share_video;

  const { current: user } = state.user;

  const { viewer_permission_list: permissionList } = state.video_player;

  return {
    show,
    validationError,
    video,
    user,
    permissionList,
  };
};

const mapDispatchToProps = dispatch => ({
  updateVideoSettings: (newSettings, password) =>
    dispatch(updateVideoSettings(newSettings, password)),
  updateVideoPermissionList: (selections, id) =>
    dispatch(updateVideoPermissionList(selections, id)),
});

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(CustomAccessSettings);
